import EA from 'helpers/init';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default EA.Methods.heroSequence = ($this) => {
    const section = $this;
    const videoWrap = section.querySelector('.hero-video-wrap');
    const video = section.querySelector('.hero-video-wrap video');
    const headerLogo = document.querySelectorAll('.header-logo');
    const sequenceWrap = document.querySelector('.hero-sequence-wrap');
    const video1 = section.querySelector('#video1');
    const canvas = document.querySelector('#sequenceCanvas');
    const heroContent = section.querySelector('#heroContent');
    const detailsContent = section.querySelector('#detailsContent');
    const statsSection = document.querySelector('.stats-section');
    const statMiddle = document.querySelector('.stat-column.middle');
    const statOuterLeft = document.querySelector('.stat-column.outer-left');
    const statOuterRight = document.querySelector('.stat-column.outer-right');
    const statSides = Array.from(document.querySelectorAll('.stat-column.side'));
    const floatingBar = document.querySelector('.floating-bar');

    if (floatingBar && statsSection) {
        const barTrigger = ScrollTrigger.create({
            id: 'floatingBar',
            trigger: statsSection,
            start: 'top bottom-=15%',
            end: 'max',
            refreshPriority: -1,
            invalidateOnRefresh: true,
            onToggle: (self) => {
                floatingBar.classList.toggle('is-hidden', self.scroll() >= self.start);
            }
        });

        const syncBar = () => {
            floatingBar.classList.toggle('is-hidden', barTrigger.scroll() >= barTrigger.start);
        };

        ScrollTrigger.addEventListener('refresh', syncBar);
        requestAnimationFrame(syncBar);
    }

    const mm = gsap.matchMedia();
    mm.add('(min-width: 1201px)', () => {

        gsap.set([statOuterLeft, statOuterRight, ...statSides, statMiddle], { autoAlpha: 0 });
        gsap.set(sequenceWrap, { opacity: 0, right: 'auto', bottom: 'auto' });

        // PHASE A: Pin hero, zoom mask, fade content
        const phaseA = gsap.timeline({
            scrollTrigger: {
                id: 'heroPin',
                trigger: section,
                start: 'top top',
                end: '+=100%',
                pin: true,
                scrub: 1,
                onLeave: () => {
                    gsap.set(sequenceWrap, {
                        opacity: 1
                    });
                },
                onEnterBack: () => {
                    gsap.set(sequenceWrap, {
                        opacity: 0,
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        borderRadius: 0
                    });
                }
            }
        });

        phaseA
            .to(videoWrap, {
                maskSize: '500% 500%',
                webkitMaskSize: '500% 500%',
                maskPosition: '66% 50%',
                webkitMaskPosition: '66% 50%',
                ease: 'none',
                duration: 1
            }, 0)
            .to(heroContent, {
                autoAlpha: 0,
                ease: 'none',
                duration: 0.1
            }, 0)
            .to(video, {
                opacity: 0,
                ease: 'power1.in',
                duration: 0.25
            }, 0.75)
            .to(headerLogo, {
                filter: 'brightness(0) invert(1)',
                ease: 'none',
                duration: 0.25
            }, 0.75);

        // PHASE B: Image sequence scrub on canvas
        if (canvas) {
            const ctx = canvas.getContext('2d');
            // Adjust frameCount for the amount of images in the sequence
            const frameCount = 132;
            const frames = new Array(frameCount);
            // Adjust path to match public URL
            const sequenceBase = canvas.dataset.sequencePath || '/img/sequence/15fps_';

            const setCanvasSize = () => {
                canvas.width = sequenceWrap.offsetWidth  || window.innerWidth;
                canvas.height = sequenceWrap.offsetHeight || window.innerHeight;
            };
            setCanvasSize();
            ScrollTrigger.addEventListener('refresh', setCanvasSize);

            const drawFrame = (index) => {
                const img = frames[index];
                if (!img?.complete || !img.naturalWidth) return;
                const { width: cw, height: ch } = canvas;
                const scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
                const x = (cw - img.naturalWidth  * scale) / 2;
                const y = (ch - img.naturalHeight * scale) / 2;
                ctx.clearRect(0, 0, cw, ch);
                ctx.drawImage(img, x, y, img.naturalWidth * scale, img.naturalHeight * scale);
            };

            const loadFrames = () => {
                frames[0] = new Image();
                frames[0].onload = () => drawFrame(0);
                frames[0].src = `${sequenceBase}00000.webp`;

                for (let i = 1; i < frameCount; i++) {
                    const img = new Image();
                    img.src = `${sequenceBase}${String(i).padStart(5, '0')}.webp`;
                    frames[i] = img;
                }
            };

            if (document.readyState === 'complete') {
                loadFrames();
            } else {
                window.addEventListener('load', loadFrames, { once: true });
            }

            ScrollTrigger.create({
                trigger: detailsContent,
                start: () => `top top-=${window.innerHeight}`,
                end: 'bottom top',
                invalidateOnRefresh: true,
                onUpdate: (self) => {
                    drawFrame(Math.min(frameCount - 1, Math.floor(self.progress * frameCount)));
                }
            });
        }

        // PHASE C: Shrink, pin stats section, reveal columns
        let targetTop = 0;
        let targetLeft = 0;
        let targetWidth = 0;
        let targetHeight = 0;

        ScrollTrigger.addEventListener('refresh', () => {
            const sr = statsSection.getBoundingClientRect();
            const mr = statMiddle.getBoundingClientRect();
            targetTop = mr.top - sr.top;
            targetLeft = mr.left - sr.left;
            targetWidth = mr.width;
            targetHeight = mr.height;
        });

        const shrinkTween = gsap.fromTo(sequenceWrap,
            {
                top: 0, left: 0,
                width: () => window.innerWidth,
                height: () => window.innerHeight
            },
            {
                top: () => targetTop,
                left: () => targetLeft,
                width: () => targetWidth,
                height: () => targetHeight,
                ease: 'power2.inOut',
                duration: 1,
                paused: true
            }
        );

        ScrollTrigger.create({
            trigger: statsSection,
            start: 'top bottom',
            end: 'top top',
            scrub: 1,
            animation: shrinkTween,
            invalidateOnRefresh: true,
            onEnter: () => {
                gsap.to(sequenceWrap, {
                    borderRadius: 8,
                    duration: 0.3
                });
                gsap.to(headerLogo, {
                    filter: 'brightness(1) invert(0)',
                    ease: 'none',
                    duration: 0.3
                });
            },
            onLeaveBack: () => {
                gsap.to(sequenceWrap, {
                    borderRadius: 0,
                    duration: 0.3
                });
                gsap.to(headerLogo, {
                    filter: 'brightness(0) invert(1)',
                    ease: 'none',
                    duration: 0.3
                });
            }
        });

        ScrollTrigger.create({
            trigger: statsSection,
            start: 'top top',
            end: '+=100%',
            pin: true,
            anticipatePin: 1
        });

        const middleTl = gsap.timeline({ paused: true })
            .fromTo([statOuterLeft, statOuterRight],
                { autoAlpha: 0, y: () => -window.innerHeight },
                { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out', stagger: 0.12 },
                0
            )
            .fromTo(statSides,
                { autoAlpha: 0, y: () => window.innerHeight },
                { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out', stagger: 0.12 },
                0
            )
            .to(statMiddle, { autoAlpha: 1, duration: 0.35, ease: 'power2.out' }, 0.65)
            .to(sequenceWrap, { opacity: 0, duration: 0.25, ease: 'power1.in' }, 0.95)
            .to({}, { duration: 0.1 });

        ScrollTrigger.create({
            trigger: statsSection,
            start: 'top top',
            end: '+=100%',
            scrub: 0.5,
            animation: middleTl,
            invalidateOnRefresh: true
        });
    });
};
